// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	MACOS_CAMERA_PERMISSION_NAME,
	MACOS_PRIVACY_AND_SECURITY_SETTINGS_NAME,
	MACOS_SYSTEM_SETTINGS_NAME,
	PREMIUM_PRODUCT_NAME,
	PRODUCT_NAME,
} from '@app/features/app/config/I18nDisplayConstants';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {isLimitToggleEnabled} from '@app/features/app/utils/LimitUtils';
import {GET_PREMIUM_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import * as PremiumModalCommands from '@app/features/premium/commands/PremiumModalCommands';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Select} from '@app/features/ui/components/form/FormSelect';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {Slider} from '@app/features/ui/components/Slider';
import type {RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import {useMediaPermission} from '@app/features/user/components/modals/tabs/hooks/useMediaPermission';
import styles from '@app/features/user/components/modals/tabs/UserVideoTab.module.css';
import * as VoiceSettingsCommands from '@app/features/voice/commands/VoiceSettingsCommands';
import {CameraPreviewModalStandalone} from '@app/features/voice/components/modals/CameraPreviewModal';
import type VoiceSettings from '@app/features/voice/state/VoiceSettings';
import type {ScreenshareResolution} from '@app/features/voice/state/VoiceSettings';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {CrownIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect} from 'react';

const CAMERA_DESCRIPTOR = msg({
	message: 'Camera {shortDeviceId}',
	comment: 'Short label in the video tab. Keep it concise. Preserve {shortDeviceId}; it is inserted by code.',
});
const DEFAULT_DESCRIPTOR = msg({
	message: 'Default',
	comment: 'Short label in the video tab. Keep it concise.',
});
const LOW_480P_LABEL = '480p';
const BEST_FOR_SLOWER_CONNECTIONS_DESCRIPTOR = msg({
	message: 'Best for slower connections',
	comment: 'Label in the video tab.',
});
const MEDIUM_720P_LABEL = '720p';
const BALANCED_QUALITY_AND_BANDWIDTH_DESCRIPTOR = msg({
	message: 'Balanced quality and bandwidth',
	comment: 'Label in the video tab. Keep the tone plain and specific.',
});
const HIGH_1080P_LABEL = '1080p';
const BEST_QUALITY_FOR_CAMERA_DESCRIPTOR = msg({
	message: 'Best quality for camera',
	comment: 'Label in the video tab.',
});
const PRODUCT_NEEDS_CAMERA_ACCESS_DESCRIPTOR = msg({
	message: '{productName} needs camera access to list your devices.',
	comment: 'Camera settings notice shown when camera permission has not been granted.',
});
const UNLOCK_HD_SCREEN_SHARE_DESCRIPTOR = msg({
	message: 'Unlock HD screen share with {premiumProductName}',
	comment: 'Premium upsell title in video settings.',
});
const HIGH_FRAME_RATE_REQUIRES_PREMIUM_DESCRIPTOR = msg({
	message: 'Frame rates above 30 FPS require {premiumProductName}',
	comment: 'Video settings note shown when higher frame rates require premium.',
});
const VERY_LOW_240P_LABEL = '240p';
const STANDARD_720P_LABEL = '720p';
const HIGH_DEFINITION_1080P_LABEL = '1080p';
const BEST_QUALITY_FOR_MOST_USERS_DESCRIPTOR = msg({
	message: 'Best quality for most users',
	comment: 'Label in the video tab.',
});
const REQUIRES_DESCRIPTOR = msg({
	message: 'Requires {premiumProductName}',
	comment:
		'Short label in the video tab. Keep it concise. Preserve {premiumProductName}; it is inserted by code. Keep the tone plain and specific.',
});
const NOT_AVAILABLE_DESCRIPTOR = msg({
	message: 'Not available',
	comment: 'Error message in the video tab.',
});
const QUAD_HD_1440P_LABEL = '1440p';
const HIGH_QUALITY_REQUIRES_FAST_CONNECTION_DESCRIPTOR = msg({
	message: 'High quality, requires fast connection',
	comment: 'Label in the video tab.',
});
const SOURCE_DESCRIPTOR = msg({
	message: 'Source',
	comment: 'Short label in the video tab for the native source resolution option. Keep it concise.',
});
const CAPTURE_AT_THE_SOURCE_S_OWN_RESOLUTION_WITH_DESCRIPTOR = msg({
	message: "Capture at the source's own resolution with no downscaling",
	comment: 'Label in the video tab.',
});
const CAMERA_2_DESCRIPTOR = msg({
	message: 'Camera',
	comment: 'Short label in the video tab. Keep it concise.',
});
const CAMERA_QUALITY_DESCRIPTOR = msg({
	message: 'Camera quality',
	comment: 'Short label in the video tab. Keep it concise.',
});
const MIRROR_CAMERA_DESCRIPTOR = msg({
	message: 'Mirror camera',
	comment: 'Switch label in the video tab for flipping the local camera preview horizontally.',
});
const SCREEN_SHARE_QUALITY_DESCRIPTOR = msg({
	message: 'Screen share quality',
	comment: 'Short label in the video tab. Keep it concise.',
});

interface VideoTabProps {
	voiceSettings: typeof VoiceSettings;
	hasPremium: boolean;
	autoRequestPermission?: boolean;
}

export const VideoTab: React.FC<VideoTabProps> = observer(
	({voiceSettings, hasPremium: _hasPremium, autoRequestPermission = true}) => {
		const {i18n} = useLingui();
		const {videoDeviceId, cameraResolution, mirrorCamera, screenshareResolution, videoFrameRate} = voiceSettings;
		const hasHigherQuality = isLimitToggleEnabled(
			{
				feature_higher_video_quality: LimitResolver.resolve({
					key: 'feature_higher_video_quality',
					fallback: 0,
				}),
			},
			'feature_higher_video_quality',
		);
		const isSelfHosted = RuntimeConfig.isSelfHosted();
		const higherQualityUnavailableDescription = !isSelfHosted
			? i18n._(REQUIRES_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})
			: i18n._(NOT_AVAILABLE_DESCRIPTOR);
		const {
			devices,
			status: permissionStatus,
			requestPermission,
		} = useMediaPermission('video', {
			autoRequest: autoRequestPermission,
		});
		useEffect(() => {
			if (videoDeviceId === 'default' && devices.length > 0) {
				VoiceSettingsCommands.update({videoDeviceId: devices[0].deviceId});
			}
		}, [devices, videoDeviceId]);
		const videoDeviceOptions =
			devices.length > 0
				? devices.map((device) => {
						const shortDeviceId = device.deviceId.slice(0, 8);
						return {
							value: device.deviceId,
							label: device.label || i18n._(CAMERA_DESCRIPTOR, {shortDeviceId}),
						};
					})
				: [{value: 'default', label: i18n._(DEFAULT_DESCRIPTOR)}];
		const effectiveVideoDeviceId = devices.length === 0 ? 'default' : videoDeviceId;
		const cameraResolutionOptions: ReadonlyArray<RadioOption<'low' | 'medium' | 'high'>> = [
			{value: 'low', name: LOW_480P_LABEL, desc: i18n._(BEST_FOR_SLOWER_CONNECTIONS_DESCRIPTOR)},
			{value: 'medium', name: MEDIUM_720P_LABEL, desc: i18n._(BALANCED_QUALITY_AND_BANDWIDTH_DESCRIPTOR)},
			{
				value: 'high',
				name: HIGH_1080P_LABEL,
				desc: hasHigherQuality ? i18n._(BEST_QUALITY_FOR_CAMERA_DESCRIPTOR) : higherQualityUnavailableDescription,
				disabled: !hasHigherQuality,
			},
		];
		const screenshareResolutionOptions: ReadonlyArray<RadioOption<ScreenshareResolution>> = [
			{value: 'low_240p', name: VERY_LOW_240P_LABEL, desc: i18n._(BEST_FOR_SLOWER_CONNECTIONS_DESCRIPTOR)},
			{value: 'low_480p', name: LOW_480P_LABEL, desc: i18n._(BEST_FOR_SLOWER_CONNECTIONS_DESCRIPTOR)},
			{
				value: 'medium',
				name: STANDARD_720P_LABEL,
				desc: i18n._(BALANCED_QUALITY_AND_BANDWIDTH_DESCRIPTOR),
			},
			{
				value: 'high',
				name: HIGH_DEFINITION_1080P_LABEL,
				desc: hasHigherQuality ? i18n._(BEST_QUALITY_FOR_MOST_USERS_DESCRIPTOR) : higherQualityUnavailableDescription,
				disabled: !hasHigherQuality,
			},
			{
				value: 'ultra',
				name: QUAD_HD_1440P_LABEL,
				desc: hasHigherQuality
					? i18n._(HIGH_QUALITY_REQUIRES_FAST_CONNECTION_DESCRIPTOR)
					: higherQualityUnavailableDescription,
				disabled: !hasHigherQuality,
			},
			{
				value: 'source',
				name: i18n._(SOURCE_DESCRIPTOR),
				desc: hasHigherQuality
					? i18n._(CAPTURE_AT_THE_SOURCE_S_OWN_RESOLUTION_WITH_DESCRIPTOR)
					: higherQualityUnavailableDescription,
				disabled: !hasHigherQuality,
			},
		];
		const handleCameraPreview = async () => {
			const granted = await requestPermission();
			if (granted) {
				ModalCommands.push(
					modal(() => (
						<CameraPreviewModalStandalone
							showEnableCameraButton={false}
							data-flx="user.video-tab.handle-camera-preview.camera-preview-modal-standalone"
						/>
					)),
				);
			}
		};
		const handleScreenshareResolutionChange = useCallback((value: ScreenshareResolution) => {
			VoiceSettingsCommands.update({screenshareResolution: value, streamingMode: 'custom'});
		}, []);
		const handleVideoFrameRateChange = useCallback((value: number) => {
			VoiceSettingsCommands.update({videoFrameRate: value, streamingMode: 'custom'});
		}, []);
		return (
			<>
				{devices.length === 0 && permissionStatus !== 'loading' && permissionStatus !== 'granted' ? (
					<div className={styles.deviceNotice} data-flx="user.video-tab.device-notice">
						<div className={styles.deviceNoticeText} data-flx="user.video-tab.device-notice-text">
							<div className={styles.deviceNoticeTitle} data-flx="user.video-tab.device-notice-title">
								<Trans>No camera found</Trans>
							</div>
							<p className={styles.deviceNoticeDescription} data-flx="user.video-tab.device-notice-description">
								{permissionStatus === 'denied' ? (
									<Trans>
										Allow {PRODUCT_NAME} to access your camera in {MACOS_SYSTEM_SETTINGS_NAME} →{' '}
										{MACOS_PRIVACY_AND_SECURITY_SETTINGS_NAME} → {MACOS_CAMERA_PERMISSION_NAME}.
									</Trans>
								) : (
									i18n._(PRODUCT_NEEDS_CAMERA_ACCESS_DESCRIPTOR, {productName: PRODUCT_NAME})
								)}
							</p>
						</div>
						<Button
							variant="secondary"
							small={true}
							onClick={() => {
								void requestPermission();
							}}
							data-flx="user.video-tab.button"
						>
							<Trans>Allow camera</Trans>
						</Button>
					</div>
				) : null}
				<div className={styles.cameraSettingsGroup} data-flx="user.video-tab.camera-settings-group">
					<div data-flx="user.video-tab.div">
						<Select
							label={i18n._(CAMERA_2_DESCRIPTOR)}
							value={effectiveVideoDeviceId}
							options={videoDeviceOptions}
							onChange={(value) => VoiceSettingsCommands.update({videoDeviceId: value})}
							data-flx="user.video-tab.select.update"
						/>
					</div>
					<div className={styles.cameraControlGroup} data-flx="user.video-tab.camera-control-group">
						<Switch
							label={i18n._(MIRROR_CAMERA_DESCRIPTOR)}
							value={mirrorCamera}
							onChange={(value) => VoiceSettingsCommands.update({mirrorCamera: value})}
						/>
						<Button
							variant="primary"
							className={styles.actionButton}
							onClick={handleCameraPreview}
							data-flx="user.video-tab.action-button.camera-preview"
						>
							<Trans>Start camera test and configure effects</Trans>
						</Button>
					</div>
				</div>
				<div data-flx="user.video-tab.div--2">
					<div className={styles.sectionTitle} data-flx="user.video-tab.section-title">
						<Trans>Camera quality</Trans>
					</div>
					<p className={styles.sectionDescription} data-flx="user.video-tab.section-description">
						<Trans>Camera quality caps at 1080p.</Trans>
					</p>
					<RadioGroup
						aria-label={i18n._(CAMERA_QUALITY_DESCRIPTOR)}
						options={cameraResolutionOptions}
						value={cameraResolution}
						onChange={(value) => VoiceSettingsCommands.update({cameraResolution: value})}
						data-flx="user.video-tab.radio-group.update"
					/>
				</div>
				<div data-flx="user.video-tab.div--3">
					<div className={styles.sectionTitle} data-flx="user.video-tab.section-title--2">
						<Trans>Screen share quality</Trans>
					</div>
					<RadioGroup
						aria-label={i18n._(SCREEN_SHARE_QUALITY_DESCRIPTOR)}
						options={screenshareResolutionOptions}
						value={screenshareResolution}
						onChange={handleScreenshareResolutionChange}
						data-flx="user.video-tab.radio-group.screenshare-resolution-change"
					/>
				</div>
				{!hasHigherQuality && !isSelfHosted && (
					<div className={styles.premiumCard} data-flx="user.video-tab.premium-card">
						<div className={styles.premiumHeader} data-flx="user.video-tab.premium-header">
							<CrownIcon
								weight="fill"
								size={18}
								className={styles.premiumIcon}
								data-flx="user.video-tab.premium-icon"
							/>
							<span className={styles.premiumTitle} data-flx="user.video-tab.premium-title">
								{i18n._(UNLOCK_HD_SCREEN_SHARE_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}
							</span>
						</div>
						<p className={styles.premiumDescription} data-flx="user.video-tab.premium-description">
							<Trans>
								Share your screen in high definition (1080p), quad HD (1440p), or native source resolution, at up to 60
								FPS.
							</Trans>
						</p>
						<Button
							variant="secondary"
							small={true}
							onClick={() => PremiumModalCommands.open()}
							data-flx="user.video-tab.button.open"
						>
							{i18n._(GET_PREMIUM_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}
						</Button>
					</div>
				)}
				<div data-flx="user.video-tab.div--5">
					<div className={styles.sectionTitle} data-flx="user.video-tab.section-title--4">
						<Trans>Frame rate</Trans>
					</div>
					<p className={styles.sectionDescription} data-flx="user.video-tab.section-description--3">
						<Trans>Applies to camera and screen share.</Trans>
					</p>
					<Slider
						defaultValue={videoFrameRate}
						factoryDefaultValue={30}
						minValue={15}
						maxValue={hasHigherQuality ? 60 : 30}
						markers={hasHigherQuality ? [15, 30, 60] : [15, 30]}
						stickToMarkers={true}
						onMarkerRender={(value) => `${Math.round(value)} FPS`}
						onValueChange={handleVideoFrameRateChange}
						showResetButton={true}
						data-flx="user.video-tab.slider"
					/>
					{!hasHigherQuality && !isSelfHosted && (
						<div className={styles.frameRateNote} data-flx="user.video-tab.frame-rate-note">
							<CrownIcon
								weight="fill"
								size={14}
								className={styles.frameRateIcon}
								data-flx="user.video-tab.frame-rate-icon"
							/>
							{i18n._(HIGH_FRAME_RATE_REQUIRES_PREMIUM_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}
						</div>
					)}
				</div>
			</>
		);
	},
);
